const PresentationDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet Presentation Layer — Controller Architecture Documentation
      </h2>

      <p className="mb-6">
        The <strong>AxisNet.Presentation</strong> layer provides the public HTTP
        interface of the AxisNet framework. All controllers exposed in this
        layer serve as the API access points for Authentication, Authorization,
        User Management, Role and Permission Management, Session Handling, and
        Logging. This layer is intentionally thin and delegates all logic to the
        Application layer through MediatR requests, ensuring complete separation
        of concerns.
      </p>

      {/* BASE CONTROLLER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          AxisNetBaseController — Core Controller Foundation
        </h3>

        <p className="mb-4">
          <strong>AxisNetBaseController</strong> is an abstract foundational
          controller that every AxisNet controller must inherit from. Its
          primary role is to centralize access to the{" "}
          <strong>MediatR pipeline</strong> by exposing a protected{" "}
          <strong>Mediator</strong>
          property.
        </p>

        <h4 className="text-lg text-white font-semibold mb-2">Why It Exists</h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            Ensures all controllers communicate exclusively through MediatR.
          </li>
          <li>Eliminates direct service injections from controllers.</li>
          <li>Guarantees consistent request handling across all modules.</li>
          <li>
            Improves testability and removes business logic from controllers.
          </li>
        </ul>

        <p className="mt-4">
          Controllers remain extremely minimal and only act as entry points,
          delegating all logic to request handlers in the Application layer.
        </p>
      </div>

      {/* CONTROLLER OVERVIEW */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Standard AxisNet Controllers — Mandatory API Endpoints
        </h3>

        <p className="mb-4">
          The controllers inside{" "}
          <strong>AxisNet.Presentation/Controllers</strong>
          are part of the AxisNet framework’s required API structure. Every
          AxisNet-based system includes these controllers by default to ensure
          complete user management, authentication workflows, and system
          administration features out of the box.
        </p>

        <h4 className="text-lg font-semibold text-white mb-4">
          Included Controllers
        </h4>

        <ul className="list-disc pl-6 space-y-4">
          <li>
            <strong>AxisNetAuthController</strong>
            <br />
            Handles authentication operations such as login, token generation,
            refresh token workflows, email confirmation, password reset, and
            security verifications.
          </li>

          <li>
            <strong>AxisNetUsersController</strong>
            <br />
            Provides user management endpoints including creation, update,
            activation, locking, searching, and retrieving user details.
          </li>

          <li>
            <strong>AxisNetRoleController</strong>
            <br />
            Exposes endpoints for managing roles, including creation, update,
            deletion, assignment, and listing.
          </li>

          <li>
            <strong>AxisNetPermissionController</strong>
            <br />
            Handles permission management, including predefined permission sets
            and custom permission operations.
          </li>

          <li>
            <strong>AxisNetUserRolesController</strong>
            <br />
            Manages user-role relationships. Assigns roles to users or removes
            roles from them. Controls high-level RBAC mappings.
          </li>

          <li>
            <strong>AxisNetUserPermissionsController</strong>
            <br />
            Handles direct permission assignments (fine-grained PBAC). Allows
            giving specific system abilities to specific users beyond
            role-level.
          </li>

          <li>
            <strong>AxisNetSessionController</strong>
            <br />
            Manages user session state, including active sessions, invalidation,
            multi-device login monitoring, session revocation, and session
            security enforcement.
          </li>

          <li>
            <strong>AxisNetLogController</strong>
            <br />
            Provides access to system logs, audit entries, diagnostic traces,
            performance logs, and exception histories.
          </li>
        </ul>

        <h4 className="text-lg text-white font-semibold mt-6 mb-2">
          Why These Controllers Are Mandatory
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            They enforce the AxisNet core feature set across all
            implementations.
          </li>
          <li>
            They ensure consistent and predictable API structure for every
            project.
          </li>
          <li>
            They standardize authentication, auditing, and authorization flows.
          </li>
          <li>
            Removing or modifying them would break base system guarantees.
          </li>
        </ul>

        <p className="mt-4">
          Projects built on AxisNet may add additional controllers for domain
          features but these base controllers represent the fundamental system
          administration suite.
        </p>
      </div>

      {/* MEDIATR COMMUNICATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-10 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Controller → MediatR → Application Workflow
        </h3>

        <p className="mb-4">
          All controllers communicate strictly through <strong>MediatR</strong>.
          Each HTTP request is converted into a MediatR command or query which
          activates:
        </p>

        <ul className="list-disc pl-6 space-y-2">
          <li>ValidationBehavior</li>
          <li>XssPreventBehavior</li>
          <li>RecaptchaBehavior</li>
          <li>AuthorizationBehavior</li>
          <li>SessionValidationBehavior</li>
          <li>RedisCacheBehavior</li>
          <li>TransactionalBehavior</li>
          <li>LogBehavior</li>
        </ul>

        <p className="mt-4 mb-2">This pipeline ensures every request is:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Sanitized</li>
          <li>Validated</li>
          <li>Secured</li>
          <li>Localized</li>
          <li>Cached (if applicable)</li>
          <li>Stored in logs</li>
          <li>Executed transactionally</li>
        </ul>

        <p className="mt-4">
          The Presentation layer is intentionally minimal, with all
          cross-cutting, business, and domain logic delegated to the Application
          and Domain layers through this mediated workflow.
        </p>
      </div>
    </section>
  );
};

export default PresentationDoc;
